import type { TreeDoc } from "../../lib/api/models";

export type DocGroup = { id: string; title: string; docs: TreeDoc[] };

const byPosition = (a: TreeDoc, b: TreeDoc) => a.position - b.position || a.title.localeCompare(b.title);

/**
 * The Overview's documents, grouped under the client's top-level pages (spec §3.1: a new client gets "Network and
 * infrastructure", "Runbooks", "Proposals and contracts" and "Handover"). Each group lists everything below its
 * page, depth first. Top-level documents that are not pages and have nothing below them go to "Other documents".
 */
export function groupByTopLevel(docs: TreeDoc[]): DocGroup[] {
  const children = new Map<string | null, TreeDoc[]>();
  for (const d of docs) {
    const list = children.get(d.parentId) ?? [];
    list.push(d);
    children.set(d.parentId, list);
  }
  const below = (id: string): TreeDoc[] =>
    [...(children.get(id) ?? [])].sort(byPosition).flatMap((d) => [d, ...below(d.id)]);
  const groups: DocGroup[] = [];
  const other: TreeDoc[] = [];
  for (const top of [...(children.get(null) ?? [])].sort(byPosition)) {
    const docsBelow = below(top.id);
    if (top.kind !== "page" && docsBelow.length === 0) other.push(top);
    else groups.push({ id: top.id, title: top.title, docs: docsBelow });
  }
  if (other.length > 0) groups.push({ id: "other", title: "Other documents", docs: other });
  return groups;
}
